import {type ChangeEvent, useCallback, useEffect, useRef, useState} from "react";
import PortForwardingTable, {type PortDataRef} from "../components/data_table/PortForwardingTable.tsx";
import {Button, Form, Modal} from "react-bootstrap";
import {swal_util} from "../lib/SwalUtil.ts";
import {instance_manager} from "../lib/InstanceManager.ts";
import {type Instance, type Port} from "../lib/ModelTypes.ts";
import {Navbar} from "../components/Navbar.tsx";
import {addPort} from "../util/PortForwardingAddUtil.tsx";
import {useTranslation} from "react-i18next";
import {network, PortForwardingAPIConnectorError} from "../lib/Network.ts";

interface ContentProps {
    toggleSidebar: () => void;
}

const PortForwarding = ({toggleSidebar}: ContentProps) => {
    const {t} = useTranslation();

    const [isAdding, setIsAdding] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [data, setData] = useState<{
        instanceOptions: Instance[],
        selectedInstance: number,
        port: string,
        proto: string,
    }>({
        instanceOptions: [],
        selectedInstance: 0,
        port: "",
        proto: "TCP",
    });

    const dataTableRef = useRef<PortDataRef>(null);

    const init = useCallback(async () => {
        try {
            await instance_manager.getInstanceList();
            setData(prev => ({...prev, instanceOptions: instance_manager.instances}));
            if (instance_manager.instances.length > 0) {
                setData(prev => ({...prev, selectedInstance: instance_manager.instances[0].id}));
            }
        } catch (err) {
            await swal_util.show_error(`${t("instance.loadFailed")} ${t("common.adminContact")}`);
            console.error(err);
        }
        if (dataTableRef.current) {
            dataTableRef.current.redraw();
        }
    }, [t]);

    async function onClickPortDelete() {
        const selectedRows = dataTableRef.current?.getSelectedData() || [];

        if (selectedRows.length === 0) {
            await swal_util.show_warning(t("portForwarding.noPortSelected"));
            return;
        }

        const confirm = await swal_util.show_question(
            t("portForwarding.deleteTitle", "포트 포워딩 삭제"),
            t(
                "portForwarding.bulkDeleteConfirm",
                "선택된 {{count}}개의 포트 포워딩 규칙을 삭제하시겠습니까?"
            ).replace("{{count}}", String(selectedRows.length))
        );
        if (!confirm.isConfirmed) return;

        try {
            swal_util.show_loading(t("common.deleting", "삭제 중입니다..."));

            let success = 0;
            const failed: { port: Port; reason: string }[] = [];

            for (const port of selectedRows) {
                try {
                    await network.deletePort(port.server_id, port.proto, port.intern_port, port.id);
                    success++;
                } catch (err) {
                    if (err instanceof PortForwardingAPIConnectorError) {
                        failed.push({ port, reason: err.localization });
                    } else {
                        failed.push({
                            port,
                            reason: t("portForwarding.errors.deleteFailedContactAdmin", "삭제 중 오류가 발생했습니다. 관리자에게 문의하세요."),
                        });
                    }
                }
            }
            await dataTableRef.current?.redraw();
            await swal_util.hide_loading();
            if (failed.length === 0) {
                await swal_util.show_success(t("common.deleted", "삭제되었습니다."));
            } else {
                const detailList = failed
                    .map(
                        ({ port, reason }) =>
                            `[${String(port.proto)}] ${String(port.intern_port)} → ${String(port.extern_port)} : ${reason}`
                    )
                    .join("<br/>");

                await swal_util.show_error(
                    t(
                        "portForwarding.partialDeleteResult",
                        "{{success}}개 성공 / {{fail}}개 실패<br/>{{details}}"
                    )
                        .replace("{{success}}", String(success))
                        .replace("{{fail}}", String(failed.length))
                        .replace("{{details}}", detailList)
                );
            }
        } catch (err) {
            try {
                await swal_util.hide_loading();
            } catch {
                /* ignore */
            }
            await swal_util.show_error(`${t("portForwarding.deletionFailed")} ${t("common.adminContact")}`);
            console.error(err);
        }
    }

    async function onClickAddPort() {
        if (isAdding) return;
        if (data.port === "") {
            await swal_util.show_warning(t("portForwarding.portRequired"));
            return;
        }
        if (Number.isNaN(Number(data.port))) {
            await swal_util.show_warning(t("portForwarding.portNumberOnly"));
            return;
        }

        setIsAdding(true);
        try {
            swal_util.show_loading(t("common.processing", "처리 중입니다..."));
            await addPort(data.selectedInstance, data.proto, Number.parseInt(data.port, 10));
            await swal_util.hide_loading();

            await swal_util.show_success(t("portForwarding.addSuccess"));
            closeModal();
            dataTableRef.current?.redraw();
        } catch (e) {
            try {
                await swal_util.hide_loading();
            } catch {
                /* ignore */
            }
            console.error(t("portForwarding.addPortFailed"), e);
            await swal_util.show_error(`${t("portForwarding.addPortFailed")} ${t("common.adminContact")}`);
        } finally {
            setIsAdding(false);
        }

        return false;
    }

    function updatePort(e: ChangeEvent<HTMLInputElement>) {
        setData((prev) => ({...prev, port: e.target.value}));
    }

    function updateProto(e: ChangeEvent<HTMLSelectElement>) {
        setData((prev) => ({...prev, proto: e.target.value}));
    }

    function updateSelectedItem(e: ChangeEvent<HTMLSelectElement>) {
        setData((prev) => ({
            ...prev,
            selectedInstance: Number.parseInt(e.target.value, 10),
        }));
    }

    function openModal() {
        setModalOpen(true);
    }

    function closeModal() {
        setModalOpen(false);
        setData((prev) => ({ ...prev, port: "", proto: "TCP" }));
    }

    useEffect(() => {
        init().finally();
    }, [init]);

    return (
        <main className="d-block col ps-2 pt-3 min-vh-100">
            <Navbar toggleSidebar={toggleSidebar}/>
            <article className="blueshift_main" id="wrapper">
                <div className="p-4 pt-2">
                    <div className="row">
                        <h6 className="text-primary pt-2 col-6">{t("title.portForwarding")}</h6>
                        <div className="text-end col-6">
                            <button type="button"
                                    className="button_blue rounded-3 py-2 mx-1"
                                    style={{fontSize: "14px"}} onClick={openModal}>
                                {t("common.new")}
                            </button>
                            <button className="button_red rounded-3 py-2"
                                    style={{fontSize: "14px"}} onClick={onClickPortDelete}>
                                {t("common.delete")}
                            </button>
                        </div>
                    </div>
                    <div className="text-center align-items-center">
                        <PortForwardingTable ref={dataTableRef}/>
                    </div>
                </div>
            </article>
            <Modal show={modalOpen} onHide={closeModal} className="modal-lg">
                <Modal.Header>
                    <Modal.Title><h6 className="text-primary pt-2">{t("title.portForwardingSettings")}</h6></Modal.Title>
                    <Button variant="white" className="btn_close" onClick={closeModal}>X
                    </Button>
                </Modal.Header>
                <Modal.Body>
                    <Form className="row" onSubmit={(e) => {e.preventDefault(); onClickAddPort();}}>
                        <div className="row mt-2">
                            <p className="col-md-5 pt-2">
                                <label htmlFor="port_instance_name">{t("instance.instanceName")}</label>
                            </p>
                            <div className="col-md-7">
                                <select name="port_instance_name" id="port_instance_name" className="form-select"
                                        defaultValue={data.selectedInstance}
                                        key={data.selectedInstance}
                                        onChange={updateSelectedItem}>
                                    {data.instanceOptions.map((option) => (
                                        <option key={option.id} value={option.id}>
                                            {option.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="row mt-2">
                            <p className="col-md-5 pt-2"><label htmlFor="protocol">{t("portForwarding.protocol")}</label></p>
                            <div className="col-md-7">
                                <select name="protocol" id="port" className="form-select" value={data.proto}
                                        onChange={updateProto}>
                                    <option value="TCP">TCP</option>
                                    <option value="UDP">UDP</option>
                                </select>
                            </div>
                        </div>
                        <div className="row mt-2">
                            <p className="col-md-5 pt-2"><label htmlFor="port">{t("portForwarding.internalPort")}</label></p>
                            <div className="col-md-7">
                                <input type="text" id="port" className="form-control text-secondary" onChange={updatePort} value={data.port}/>
                            </div>
                        </div>
                        <div className="row mt-2">
                            <div className="col-md-12">
                                <button
                                    className="button_blue py-1 rounded-3 w-100"
                                    style={{marginTop: "2px"}}
                                    type="button"                 // FIXME: 버튼을 일반 버튼으로 임시 수정함.
                                    onClick={onClickAddPort}
                                    disabled={isAdding}
                                >
                                    <p style={{fontSize: "14px", fontWeight: "300"}}>{t("common.add")}</p>
                                </button>
                            </div>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
        </main>
    );
}

export default PortForwarding;